import { useBrandSearchApi } from 'hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BRAND_SEARCH_DEBOUNCE_MS, BRAND_SEARCH_MIN_LENGTH } from '../lib/constants';
import type { BrandName } from '../store/brandNamesSlice';
import useCustomAudiencesStore from '../store/useCustomAudiencesStore';

/**
 * Where a brand search stands.
 *
 * - `short`: the text is under `BRAND_SEARCH_MIN_LENGTH`, so nothing is sent.
 * - `searching`: waiting out `BRAND_SEARCH_DEBOUNCE_MS`, or the request is in flight.
 * - `done`: `results` holds the latest search's brands.
 * - `error`: the latest search failed.
 */
export type BrandSearchStatus = 'short' | 'searching' | 'done' | 'error';

/** A brand search match: its name for chips, and its domain to tell terse names apart in the options. */
export type BrandSearchResult = BrandName & { domain: string | null };

/**
 * Debounced search over active proxima-platform brands, through Nova's `useBrandSearchApi`.
 *
 * Only the latest search's answer is kept, and its brands are added to the store's brand names.
 *
 * proxima-platform matches the whole text as a substring of a brand's name or domain, so a spaced query such as "nimbus designs" misses the domain `nimbusdesigns.example`.
 * When a query with spaces finds nothing, it is sent once more with the spaces removed.
 */
export const useBrandSearch = () => {
  const { getBrands, error } = useBrandSearchApi();
  const getBrandsRef = useRef(getBrands);
  getBrandsRef.current = getBrands;
  const rememberBrandNames = useCustomAudiencesStore(state => state.rememberBrandNames);
  const [results, setResults] = useState<BrandSearchResult[]>([]);
  const [pending, setPending] = useState(false);
  const [searchable, setSearchable] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const latest = useRef(0);

  useEffect(
    () => () => {
      clearTimeout(timer.current);
      latest.current += 1;
    },
    []
  );

  const search = useCallback(
    (text: string) => {
      const term = text.trim();
      const requestId = ++latest.current;
      clearTimeout(timer.current);
      const long = term.length >= BRAND_SEARCH_MIN_LENGTH;
      setSearchable(long);
      setPending(long);
      if (!long) {
        setResults([]);
        return;
      }
      timer.current = setTimeout(async () => {
        let brands = await getBrandsRef.current({ search: term });
        if (requestId !== latest.current) return;
        const compact = term.replace(/\s+/g, '');
        if (brands.length === 0 && compact !== term) {
          brands = await getBrandsRef.current({ search: compact });
          if (requestId !== latest.current) return;
        }
        const named: BrandSearchResult[] = brands
          .filter(brand => brand.brandId && brand.name)
          .map(({ brandId, name, domain }) => ({ brandId, name, domain: domain || null }));
        rememberBrandNames(named.map(({ brandId, name }) => ({ brandId, name })));
        setResults(named);
        setPending(false);
      }, BRAND_SEARCH_DEBOUNCE_MS);
    },
    [rememberBrandNames]
  );

  let status: BrandSearchStatus = 'done';
  if (!searchable) status = 'short';
  else if (pending) status = 'searching';
  else if (error) status = 'error';

  return { results, status, search };
};
